import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import i18next from "i18next";
import { axiosInstance } from '../../axios/axiosHandler';
import { EndPoints } from '../../axios/endPoints';
import { OptionsObject } from 'notistack';
import { DEFAULT_AUTO_HIDE_DURATION, getRandomKey } from '../../components/notifications/_common';

export interface Language {
  id: number;
  name: string;
  code: string;
  default: boolean;
}

interface Notification {
  message?: string;
  options: OptionsObject;
  dismissed: boolean;
}

interface AppState {
  notifications: Notification[];
  languages: Language[],
  selectedLanguage: number | '' | undefined,
  selectedLanguageName: string | null | undefined,
  selectedLanguageCode: string | null | undefined, 
}

const initialState: AppState = {
  notifications: [],

  languages: [] as Language[],
  selectedLanguage: '' as number | '' | undefined,
  selectedLanguageName: null as string | null | undefined,
  selectedLanguageCode: null as string | null | undefined, 
};
export const getLanguages = createAsyncThunk(
  'GetLanguages',
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(EndPoints.GET_LANGUAGES);
      return await response.data as Language[];
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const appSlice = createSlice({
  name: 'app',
  initialState: initialState,
  reducers: {
    enqueueSnackbar: (state, action: PayloadAction<any>) => {
      state.notifications = [
        ...state.notifications,
        {
          ...action.payload,
          options: {
            ...action.payload.options,
            key: getRandomKey(),
            autoHideDuration: DEFAULT_AUTO_HIDE_DURATION,
          },
        },
      ];
    },

    removeSnackbar: (state, action) => {
      state.notifications = [
        ...state.notifications.filter(
          (notification) => notification.options.key !== action.payload
        ),
      ];
    },
    handleSelectedLanguageChange: (state, action) => {
      const language = action.payload;
      state.selectedLanguage = language.id;
      state.selectedLanguageName = language.name;
      state.selectedLanguageCode = language.code;
      localStorage.setItem('languageId', language.id);
      localStorage.setItem('languageCode', language.code);
    }
  },
  extraReducers: (builder) => {
    //GET LANGUAGES
    builder.addCase(getLanguages.fulfilled, (state, action) => {
      const languages = action.payload;
      state.languages = languages;
      
      const defaultLanguage = languages.find((el : Language) => el.default);
      if(defaultLanguage && !localStorage.getItem('languageId')) 
      {
        state.selectedLanguage = defaultLanguage?.id;
        state.selectedLanguageName = defaultLanguage?.name;
        state.selectedLanguageCode = defaultLanguage?.code;

        localStorage.setItem('languageId', defaultLanguage?.id.toString());
        localStorage.setItem('languageCode', defaultLanguage?.code || 'en');
      }
      else if(localStorage.getItem('languageId')) {
        const languageId = parseInt(localStorage.getItem('languageId')!);
        const language = languages.find((el) => el.id === languageId);

        if(language) {
          state.selectedLanguage = language?.id;
          state.selectedLanguageName = language?.name;
          state.selectedLanguageCode = language?.code;
          localStorage.setItem('languageCode', language?.code || 'en');
          i18next.changeLanguage(language.code);
        }
      }
    });
    builder.addCase(getLanguages.rejected, (state, action) => {
      state.languages = [];
    });
  }
});

export const { enqueueSnackbar, removeSnackbar, handleSelectedLanguageChange } = appSlice.actions;

export const appSelector = (state: any) => state.app;
